/** Who a section's settings belong to: the groups of the sheet's column, in its order. */
export const SETTINGS_OWNERS = ['you', 'workspace', 'project'] as const
export type SettingsOwner = (typeof SETTINGS_OWNERS)[number]

/** A section's address. A project's names the project: `/settings/projects/{id}/{section}`. */
export function settingsPath(owner: SettingsOwner, section: string, project?: string): string {
  return owner === 'project' ? `/settings/projects/${encodeURIComponent(project ?? '')}/${section}` : `/settings/${owner}/${section}`
}

/** What comes after `/settings` in an address, decoded. */
function parts(pathname: string): string[] {
  return pathname.split('/').filter(Boolean).slice(1).map((part) => {
    try { return decodeURIComponent(part) } catch { return part }
  })
}

/** The section an address names, as written: whether there is one is the registry's to say. */
export function readSettingsPath(pathname: string): { owner?: string; section?: string; projectId?: string } {
  const [first, second, third, ...rest] = parts(pathname)
  if (first === 'projects') return third !== undefined && rest.length === 0 ? { owner: 'project', projectId: second, section: third } : {}
  return second !== undefined && third === undefined ? { owner: first, section: second } : {}
}

/**
 * Where the sections went that the column no longer lists (SUN-1454, the
 * content pass): keep only the moves whose destination exists here.
 */
const MOVED: Readonly<Record<string, string>> = {
  'you/keys': 'you/account',
  'workspace/name': 'workspace/projects',
}

/**
 * Where an address that names no section of today goes: `/settings` itself,
 * an owner alone, the workspace and project pages Settings had before the
 * sheet, and the supported sections that have moved since. Null for
 * any other address. `project` is the one the board shows, for the old page
 * that followed it.
 */
export function movedSettingsPath(pathname: string, project: string | undefined): string | null {
  const [first, second, third, ...rest] = parts(pathname)
  if (rest.length) return null
  if (third !== undefined) return null
  if (first === 'projects' && second !== undefined) return settingsPath('project', 'repository', second)
  if (first === 'workspace' && second === 'labels' && third === undefined) return project ? settingsPath('project', 'labels', project) : settingsPath('workspace', 'projects')
  if (second !== undefined) {
    const to = MOVED[`${first}/${second}`]
    return to ? `/settings/${to}` : null
  }
  if (first === undefined || first === 'you') return settingsPath('you', 'account')
  if (first === 'workspace') return settingsPath('workspace', 'projects')
  if (first === 'project' || first === 'projects') return project ? settingsPath('project', 'repository', project) : settingsPath('workspace', 'projects')
  return null
}
